/**
 * Google Analytics 4 (GA4) Tracking Utility
 * 
 * This module provides functions to track events and page views
 * throughout the Grow and Stay Rich website.
 * 
 * Privacy-compliant setup with IP anonymization enabled.
 */

// Initialize Google Analytics
export const initGA = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') {
    return;
  }

  // Check if GA is already initialized
  if (window.gtag) {
    return;
  }

  // Load Google Analytics script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  
  // Configure GA4 with privacy settings
  gtag('config', measurementId, {
    // IP anonymization for privacy compliance
    anonymize_ip: true,
    // Cookie settings
    cookie_flags: 'SameSite=None;Secure',
    // Send page views automatically
    send_page_view: true,
    // Data retention (2 months for privacy compliance)
    // Note: This is set in GA4 admin, but documented here
  });

  return gtag;
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title = '') => {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!window.gtag) return;

  window.gtag('event', eventName, {
    ...eventParams,
    // Add timestamp
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track form submission
 * @param {string} formName - Name/ID of the form
 * @param {string} formType - Type of form (contact, booking, newsletter, etc.)
 */
export const trackFormSubmission = (formName, formType) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_type: formType,
    event_category: 'Form',
    event_label: `${formType} - ${formName}`,
  });
};

/**
 * Track button/link click
 * @param {string} buttonName - Name/label of the button
 * @param {string} buttonLocation - Where the button is located
 * @param {string} destination - Where the button leads
 */
export const trackButtonClick = (buttonName, buttonLocation, destination = '') => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    destination: destination,
    event_category: 'Engagement',
    event_label: `${buttonName} - ${buttonLocation}`,
  });
};

/**
 * Track file download
 * @param {string} fileName - Name of the file
 * @param {string} fileType - Type of file (pdf, doc, etc.)
 */
export const trackDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    event_category: 'Download',
    event_label: fileName,
  });
};

/**
 * Track lead magnet download
 * @param {string} leadMagnetName - Name of the lead magnet
 */
export const trackLeadMagnet = (leadMagnetName) => {
  trackEvent('lead_magnet_download', {
    lead_magnet_name: leadMagnetName,
    event_category: 'Lead Generation',
    event_label: leadMagnetName,
    // Mark as conversion
    conversion: true,
  });
};

/**
 * Track consultation booking
 */
export const trackBooking = () => {
  trackEvent('consultation_booking', {
    event_category: 'Conversion',
    event_label: 'Free Consultation',
    // Mark as conversion
    conversion: true,
  });
};

/**
 * Track testimonial submission
 */
export const trackTestimonialSubmission = () => {
  trackEvent('testimonial_submit', {
    event_category: 'Engagement',
    event_label: 'Testimonial Submission',
  });
};

/**
 * Track newsletter signup
 */
export const trackNewsletterSignup = () => {
  trackEvent('newsletter_signup', {
    event_category: 'Lead Generation',
    event_label: 'Newsletter Subscription',
    // Mark as conversion
    conversion: true,
  });
};

/**
 * Track webinar registration
 */
export const trackWebinarRegistration = () => {
  trackEvent('webinar_registration', {
    event_category: 'Lead Generation',
    event_label: 'Webinar Registration',
    // Mark as conversion
    conversion: true,
  });
};

/**
 * Track scroll depth
 * @param {number} depth - Scroll depth percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (depth) => {
  trackEvent('scroll_depth', {
    scroll_depth: depth,
    event_category: 'Engagement',
    event_label: `${depth}% scroll`,
  });
};

/**
 * Track video play
 * @param {string} videoTitle - Title of the video
 * @param {string} videoLocation - Where the video is located
 */
export const trackVideoPlay = (videoTitle, videoLocation) => {
  trackEvent('video_play', {
    video_title: videoTitle,
    video_location: videoLocation,
    event_category: 'Engagement',
    event_label: videoTitle,
  });
};

/**
 * Track blog post view
 * @param {string} postTitle - Title of the blog post
 * @param {string} postSlug - Slug of the blog post
 */
export const trackBlogPostView = (postTitle, postSlug) => {
  trackEvent('blog_post_view', {
    post_title: postTitle,
    post_slug: postSlug,
    event_category: 'Content',
    event_label: postTitle,
  });
};

/**
 * Track external link click
 * @param {string} linkUrl - URL of the external link
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (linkUrl, linkText) => {
  trackEvent('external_link_click', {
    link_url: linkUrl,
    link_text: linkText,
    event_category: 'Outbound',
    event_label: linkText,
  });
};

